import type { SearchUpdateMessageDatagramBuf } from "~/classes/generated/message"
import type { SearchUpdateResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SearchUpdateRequestDatagramBuf } from "~/classes/generated/request"
import { StaticSearch } from "~/classes/search/StaticSearch"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { parseSearchId } from "~/utils/flatbuffers/id"
import { addSearchToBuf, parseSearch } from "~/utils/flatbuffers/search"
import { maybe } from "~/utils/helpers"

const console = useLogger("search/update")

export function sendSearchUpdateRequests(requests: SearchUpdateRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)

  const requestVector = []
  for (const request of requests) {
    const search = addSearchToBuf(builder, request.search)
    const requestId = addRequestIdToBuf(builder, request.requestId)
    SearchUpdateRequestDatagramBuf.startSearchUpdateRequestDatagramBuf(builder)
    SearchUpdateRequestDatagramBuf.addRequestId(builder, requestId)
    SearchUpdateRequestDatagramBuf.addSearch(builder, search)
    SearchUpdateRequestDatagramBuf.addOffline(builder, request.offline)
    const updateRequest = SearchUpdateRequestDatagramBuf.endSearchUpdateRequestDatagramBuf(builder)
    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addSearchUpdateRequest(builder, updateRequest)
    requestVector.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const vector = DatagramBuf.createRequestVector(builder, requestVector)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, vector)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleSearchUpdateResponseDatagramBuf(datagram: SearchUpdateResponseDatagramBuf) {
  // TODO: get rid of datagram result, and find out a better way to
  // error out of this, since we need to send notifications
  onSearchUpdateResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    search: maybe(parseSearch)(datagram.search()),
    searchId: parseSearchId(datagram.searchId()!),
    offline: false,
  })
}

export function onSearchUpdateResponse(response: SearchUpdateResponseDatagram) {
  const { emitEntityEvent } = useEvents()
  if (response.error)
    return onSearchUpdateErrored(response.error)
  if (!response.search)
    throw new Error("No search in update response")

  const iSearch = new StaticSearch(response.search)
  console.debug("Search updated", response.search)

  emitEntityEvent("searchProperties", iSearch.id, iSearch.inner(), response.offline)
}

export function handleSearchUpdateMessageDatagramBuf(datagram: SearchUpdateMessageDatagramBuf) {
  onSearchUpdateMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    search: parseSearch(datagram.search()!),
  })
}

export function onSearchUpdateMessage(message: SearchUpdateMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  const iSearch = new StaticSearch(message.search)
  console.debug("Search updated", message.search)

  emitEntityEvent("searchProperties", iSearch.id, iSearch.inner(), false)
}

export function onSearchUpdateErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()
  return notifyDatagramError(error, "Search update was unsuccessful")
}
